import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { QuestionCarouselNav } from "components/shared/QuestionCarouselNav/QuestionCarouselNav";
import { buildPreviewChallenge, colors } from "utils";

// Translate a LiveQuestionLoop (from the SSE stream) into the `challenge`
// shape ConsensusActivityRender expects. The render component does the rest:
// HTML prompt, image, MC pills, OR fill-in, fraction render, etc.
export const buildChallengeFromLoop = (loop) => {
  if (!loop) return null;
  const answerOptions = Array.isArray(loop.answer_options) ? loop.answer_options : [];
  const isMc = loop.question_type === "multiple_choice" && answerOptions.length > 0;
  return {
    id: loop.question_id,
    source: "LIVE_PILL",
    external_id: null,
    prompt: loop.question_prompt || "",
    spoken_prompt: loop.question_spoken_prompt || null,
    answer_options: isMc ? answerOptions : null,
    solution_index: isMc ? loop.solution_index ?? null : null,
    solution: isMc ? null : loop.correct_answer ?? null,
    internal_image_url: loop.question_internal_image_url || null,
    prompt_image_url: loop.question_image_url || null,
    prompt_image_latex: loop.question_image_latex ?? null,
    render_type: loop.question_render_type || "fraction_legacy",
    standards: loop.question_standards || [],
    difficulty: loop.question_difficulty ?? 1,
    status: "active",
    randomize_options: false,
  };
};

// Translate submissions into the studentResponses shape ConsensusActivityRender
// renders student dots from. For MC, response.option_index is the index in
// answer_options; for OR, response.solution is the literal answer text.
const buildStudentResponsesFromLoop = (loop) => {
  if (!loop?.submissions?.length) return null;
  const isMc =
    loop.question_type === "multiple_choice" &&
    Array.isArray(loop.answer_options) &&
    loop.answer_options.length > 0;
  const out = {};
  loop.submissions.forEach((s, i) => {
    const key = s.student_id || `s-${i}`;
    if (isMc) {
      const idx = loop.answer_options.indexOf(s.answer);
      out[key] = {
        option_index: idx >= 0 ? idx : null,
        oko_user_id: s.student_id,
        student_name: s.student_name,
      };
    } else {
      out[key] = {
        solution: s.answer,
        oko_user_id: s.student_id,
        student_name: s.student_name,
      };
    }
  });
  return out;
};

export const LiveQuestionModal = ({ show, onHide, session, initialQuestionId }) => {
  const window = useMemo(
    () => (session?.window ? [...session.window].reverse() : []),
    [session?.window],
  );
  // Denominator is "questions answered so far", not the lesson total. The
  // highest question_order in the window is the most recent completed loop;
  // if backend ever exposes question_order skips (e.g. the educator advanced
  // past unanswered ones) this still tracks the visible count correctly.
  const answeredSoFar = useMemo(
    () =>
      window.reduce(
        (max, l) => Math.max(max, l?.question_order || 0),
        window.length,
      ),
    [window],
  );

  const [index, setIndex] = useState(0);

  // Center the modal on the question the user clicked the moment it opens, or
  // when the caller changes initialQuestionId without remounting.
  useEffect(() => {
    if (!show) return;
    const idx = window.findIndex((l) => l.question_id === initialQuestionId);
    setIndex(idx >= 0 ? idx : 0);
  }, [show, initialQuestionId, window]);

  const loop = window[index] || null;
  const challenge = useMemo(() => buildChallengeFromLoop(loop), [loop]);
  const studentResponses = useMemo(() => buildStudentResponsesFromLoop(loop), [loop]);
  const previewChallenge = useMemo(
    () => (challenge ? buildPreviewChallenge(challenge) : null),
    [challenge],
  );

  if (!show) return null;

  const canPrev = index > 0;
  const canNext = index < window.length - 1;
  const handlePrev = () => canPrev && setIndex(index - 1);
  const handleNext = () => canNext && setIndex(index + 1);

  const displayOrder = loop?.question_order ?? index + 1;
  const displayTotal = answeredSoFar || window.length;

  return (
    <StyledModal show={show} onHide={onHide} size="lg">
      <ModalHeader>
        <QuestionCarouselNav
          onPrev={handlePrev}
          onNext={handleNext}
          canPrev={canPrev}
          canNext={canNext}
        >
          <Position>
            <PositionLabel>Question</PositionLabel>
            <PositionValue>
              {displayOrder} of {displayTotal}
            </PositionValue>
          </Position>
        </QuestionCarouselNav>
        <CloseButton onClick={onHide} aria-label="Close">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </ModalHeader>
      <ModalBody>
        {!loop ? (
          <EmptyState>
            <p>This question isn't in the live window yet.</p>
            <small>
              Older questions will be fetchable once the live-session detail
              endpoint ships.
            </small>
          </EmptyState>
        ) : (
          <RenderWrapper>
            <ConsensusActivityRender
              challenge={previewChallenge}
              studentResponses={studentResponses}
              onMCAnswer={() => {}}
              onORAnswer={() => {}}
              isMathJaxSet={false}
              showBorder
              showPromptFormula
              sessionId={session?.session_id}
            />
          </RenderWrapper>
        )}
      </ModalBody>
    </StyledModal>
  );
};

export default LiveQuestionModal;

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 820px;
    margin: 40px auto;
    max-height: calc(100vh - 80px);
  }
  .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 12px 32px rgba(14, 26, 49, 0.18);
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 80px);
    overflow: hidden;
  }
`;

const ModalHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 20px 24px 12px;
`;

const Position = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
`;

const PositionLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #6B6967;
`;

const PositionValue = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #6B6967;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${colors.nightlyWoods500};
  background: #FFFFFF;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  &:hover {
    background: ${colors.nightlyWoods100};
  }
  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: 12px 24px 28px;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const RenderWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  font-family: Inter, sans-serif;
  color: ${colors.nightlyWoods500};
  p {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
  }
  small {
    font-size: 13px;
    color: #6B6967;
  }
`;
